"use client"

import * as React from "react"
import { GripVertical } from "lucide-react"

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { ResultProfile, type ResultProfileData } from "@/components/search/graphite-result-profile"
import type { Verdict } from "@/components/search/graphite-search-results"
import {
  barsFor,
  careerSummary,
  continuousSegments,
  leadRole,
  toRoles,
  type Axis,
} from "@/lib/career-track"
import { useGetSearchEnrichProfilesCandidateId } from "openapi/api/endpoints"

/**
 * A search result opened over the results — canvas 29a, with 30d as the phone.
 *
 * The shell is unchanged: 860px by default, dragged between 520 and 1200, and
 * the width you set is remembered. Everything inside it is turn 29's, which
 * says of what was here before: "The old sheet stacked six nested cards and
 * repeated the result card's fields underneath it."
 *
 * This owns the fetch and the geometry; `ResultProfile` draws.
 */

interface CandidateProfilePanelProps {
  candidateId: string | null
  searchHash: string
  isOpen: boolean
  onClose: () => void
  /** The page's shared axis, so the sheet and the row it came from agree. */
  axis?: Axis
  verdict?: Verdict
  /** Already on the job this search feeds — the sheet says so too. */
  isAdded?: boolean
  /** The linked-job lookup is still running; a Yes cannot land yet. */
  isResolving?: boolean
  onVerdict?: (candidateId: string, verdict: Verdict) => void
  /** 1-based position in the visible results, for walking without closing. */
  position?: number
  total?: number
  onPrevious?: () => void
  onNext?: () => void
}

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n))

export function CandidateProfilePanel({
  candidateId,
  searchHash,
  isOpen,
  onClose,
  axis,
  verdict = "none",
  isAdded,
  isResolving,
  onVerdict,
  position,
  total,
  onPrevious,
  onNext,
}: CandidateProfilePanelProps) {
  const { data: candidate } = useGetSearchEnrichProfilesCandidateId(
    candidateId as string,
    { search_hash: searchHash },
    { query: { enabled: !!candidateId && !!searchHash && isOpen } },
  )

  const profile = candidate?.profile
  const intelligence = candidate?.talentIntelligence

  // --- Width / resize ---------------------------------------------------------
  // Default wider. This will be used on desktop/tablet; mobile ignores this.
  const DEFAULT_WIDTH = 860
  const MIN_WIDTH = 520
  const MAX_WIDTH = 1200

  const [panelWidth, setPanelWidth] = React.useState<number>(DEFAULT_WIDTH)
  const [isResizing, setIsResizing] = React.useState(false)

  // Track viewport to disable resizing on small screens
  const [isMobile, setIsMobile] = React.useState(false)
  React.useEffect(() => {
    const update = () => setIsMobile(window.matchMedia("(max-width: 640px)").matches)
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])

  const beginResize = React.useCallback((clientX: number) => {
    if (isMobile) return
    setIsResizing(true)

    const startX = clientX
    const startWidth = panelWidth

    const onMove = (e: MouseEvent) => {
      // Dragging handle left increases width (right sheet)
      const delta = startX - e.clientX
      const next = clamp(startWidth + delta, MIN_WIDTH, Math.min(MAX_WIDTH, Math.floor(window.innerWidth * 0.95)))
      setPanelWidth(next)
    }

    const onUp = () => {
      setIsResizing(false)
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }

    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }, [panelWidth, isMobile])

  // Optional: persist width between opens
  React.useEffect(() => {
    const key = "candidateProfilePanelWidth"
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(key) : null
    if (saved) setPanelWidth(clamp(Number(saved), MIN_WIDTH, MAX_WIDTH))
  }, [])
  React.useEffect(() => {
    const key = "candidateProfilePanelWidth"
    if (!isMobile) window.localStorage.setItem(key, String(panelWidth))
  }, [panelWidth, isMobile])


  const roles = React.useMemo(
    () => toRoles(profile?.experiences),
    [profile?.experiences],
  )

  /*
    The sheet can be opened before the page has an axis (a direct link, a page
    still loading), so it falls back to one drawn from this candidate alone.
    Comparability is the page's job; legibility is this component's.
  */
  const ownAxis = React.useMemo<Axis>(() => {
    const from = roles.length > 0 ? roles[0].startYear : new Date().getFullYear() - 10
    const to = new Date().getFullYear()
    const span = Math.max(1, to - from)
    const step = span > 12 ? 4 : span > 6 ? 2 : 1
    const ticks = []
    for (let year = Math.ceil((from + 1) / step) * step; year < to; year += step) {
      ticks.push({ year, left: ((year - from) / span) * 100 })
    }
    return { from, to, isOpen: true, ticks }
  }, [roles])

  const trackAxis = axis ?? ownAxis

  const data = React.useMemo<ResultProfileData>(() => {
    const latest = leadRole(roles)
    const now = new Date().getFullYear()

    const highlights = (intelligence?.summary ?? [])
      .flatMap((reason) => reason?.highlights ?? [])
      .map((phrase) => phrase.trim().toLowerCase())
      .filter(Boolean)

    const skills = profile?.skills ?? []

    /*
      Only facts the contract carries. 29a also draws a Contact row; nothing on
      the enriched profile is an email address, so that row is not drawn rather
      than filled with something that looks like one.
    */
    const facts: { label: string; value: string }[] = []
    const location =
      profile?.location_str ??
      [profile?.city, profile?.country_full_name ?? profile?.country]
        .filter(Boolean)
        .join(", ")
    if (location) facts.push({ label: "Location", value: location })
    if (profile?.languages?.length) {
      facts.push({ label: "Languages", value: profile.languages.join(", ") })
    }
    const education = profile?.education?.[0]
    if (education?.school) {
      const degree = [education.degree_name, education.field_of_study]
        .filter(Boolean)
        .join(" ")
      facts.push({
        label: "Education",
        value: degree ? `${degree}, ${education.school}` : education.school,
      })
    }

    const inRole = latest
      ? Math.max(0, (latest.endYear ?? now) - latest.startYear)
      : undefined
    const total = careerSummary(roles, now)

    // The phone's track: their own span, segments abutting, no shared scale.
    const own = continuousSegments(roles, now)

    return {
      candidateId: candidateId ?? "",
      name: profile?.full_name ?? "Candidate",
      initials:
        `${profile?.first_name?.[0] ?? ""}${profile?.last_name?.[0] ?? ""}`.toUpperCase() ||
        (profile?.full_name?.[0] ?? "?").toUpperCase(),
      location: location || undefined,
      currentRole:
        latest?.title && latest?.company
          ? `${latest.title} · ${latest.company}`
          : (latest?.title ?? profile?.headline ?? undefined),
      tenureLine:
        inRole !== undefined && total
          ? `${inRole} year${inRole === 1 ? "" : "s"} in role · ${total}`
          : total,
      careerLine: total,
      roles,
      bars: barsFor(roles, trackAxis),
      segments: own.segments,
      ownAxis: { from: own.from, to: own.to, isOpen: own.isOpen, ticks: own.ticks },
      facts,
      skills,
      matchedSkills: new Set(
        skills
          .map((skill) => skill.toLowerCase())
          .filter((skill) =>
            highlights.some(
              (phrase) => phrase.includes(skill) || skill.includes(phrase),
            ),
          ),
      ),
      reasons: (intelligence?.summary ?? [])
        .map((reason) => ({
          text: reason?.text ?? "",
          highlights: reason?.highlights ?? [],
        }))
        .filter((reason) => reason.text.trim().length > 0),
    }
  }, [candidateId, intelligence?.summary, profile, roles, trackAxis])

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        side="right"
        className={["p-0", "w-full sm:max-w-none", isResizing ? "select-none" : ""].join(" ")}
        style={isMobile ? { width: "100vw" } : { width: `${panelWidth}px` }}
      >
        {/* The sheet primitive needs a title for its own labelling; 29a draws
            the identity itself, in the header below. */}
        <SheetTitle className="sr-only">{data.name}</SheetTitle>

        <div className="relative flex h-full flex-col">
          {!isMobile && (
            <div
              className="absolute left-0 top-0 z-10 h-full w-2 cursor-col-resize"
              onMouseDown={(e) => beginResize(e.clientX)}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel"
            >
              <div className="absolute left-0 top-1/2 h-14 w-2 -translate-y-1/2 rounded-full bg-border/60 hover:bg-border" />
              <GripVertical className="absolute left-[-6px] top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground/70" />
            </div>
          )}

          <ResultProfile
            data={data}
            axis={trackAxis}
            verdict={verdict}
            isAdded={isAdded}
            isResolving={isResolving}
            onVerdict={(next) => candidateId && onVerdict?.(candidateId, next)}
            position={position}
            total={total}
            onPrevious={onPrevious}
            onNext={onNext}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
